<script setup>
import { computed, onMounted, ref } from 'vue';
import { endOfDay, getUnixTime, startOfDay, subDays } from 'date-fns';
import { useI18n } from 'vue-i18n';
import ReportsAPI from 'dashboard/api/reports';
import { downloadCsvFile } from 'dashboard/helper/downloadHelper';
import ReportHeader from './components/ReportHeader.vue';
import SearchDateRangeSelector from 'dashboard/modules/search/components/SearchDateRangeSelector.vue';

import NextButton from 'dashboard/components-next/button/Button.vue';

const { t, locale } = useI18n();

const filters = ref({
  dateRange: {
    type: 'last_30_days',
    from: getUnixTime(startOfDay(subDays(new Date(), 30))),
    to: getUnixTime(endOfDay(new Date())),
  },
});

const isLoading = ref(false);
const isExporting = ref(false);
const rows = ref([]);

const localeCode = computed(() => locale.value?.replace('_', '-') || 'es');

const payload = computed(() => ({
  from: filters.value.dateRange?.from,
  to: filters.value.dateRange?.to,
}));

const formatDate = value => {
  if (!value) return '--';

  const [year, month, day] = value.split('-').map(Number);

  return new Intl.DateTimeFormat(localeCode.value, {
    dateStyle: 'medium',
  }).format(new Date(year, month - 1, day));
};

const formatTime = value => {
  if (!value) return '--';

  return new Intl.DateTimeFormat(localeCode.value, {
    timeStyle: 'short',
  }).format(new Date(value));
};

const fetchReport = async () => {
  try {
    isLoading.value = true;
    const { data } = await ReportsAPI.getAgentLoginTimes(payload.value);
    rows.value = data.entries || [];
  } finally {
    isLoading.value = false;
  }
};

const exportReport = async () => {
  try {
    isExporting.value = true;
    const { data } = await ReportsAPI.downloadAgentLoginTimes(payload.value);
    const timestamp = filters.value.dateRange?.to || getUnixTime(new Date());
    downloadCsvFile(`agent-login-times-${timestamp}.csv`, data);
  } finally {
    isExporting.value = false;
  }
};

onMounted(fetchReport);
</script>

<template>
  <div class="flex flex-col gap-5 pb-8">
    <ReportHeader
      :header-title="$t('AGENT_LOGIN_REPORTS.HEADER')"
      :header-description="$t('AGENT_LOGIN_REPORTS.DESCRIPTION')"
    >
      <NextButton
        slate
        :label="$t('AGENT_LOGIN_REPORTS.DOWNLOAD')"
        icon="i-lucide-download"
        :is-loading="isExporting"
        @click="exportReport"
      />
    </ReportHeader>

    <div class="rounded-xl border border-n-weak bg-n-alpha-1 p-4">
      <SearchDateRangeSelector
        v-model="filters.dateRange"
        class="min-w-0 max-w-full"
        @change="fetchReport"
      />
    </div>

    <div
      v-if="isLoading"
      class="flex flex-col items-center justify-center rounded-xl border border-dashed border-n-weak bg-n-alpha-1 px-6 py-10 text-center"
    >
      <p class="m-0 text-n-slate-11">
        {{ $t('AGENT_LOGIN_REPORTS.LOADING') }}
      </p>
    </div>

    <div
      v-else-if="rows.length === 0"
      class="flex flex-col items-center justify-center rounded-xl border border-dashed border-n-weak bg-n-alpha-1 px-6 py-10 text-center"
    >
      <p class="m-0 text-n-slate-11">
        {{ $t('AGENT_LOGIN_REPORTS.EMPTY_STATE') }}
      </p>
    </div>

    <section v-else class="rounded-xl border border-n-weak bg-n-solid-1 p-4">
      <div class="overflow-x-auto">
        <table class="w-full text-sm">
          <thead>
            <tr class="text-left text-n-slate-11 border-b border-n-weak">
              <th class="py-2 pr-3">{{ t('AGENT_LOGIN_REPORTS.COLUMNS.AGENT') }}</th>
              <th class="py-2 pr-3">{{ t('AGENT_LOGIN_REPORTS.COLUMNS.DATE') }}</th>
              <th class="py-2 pr-3">{{ t('AGENT_LOGIN_REPORTS.COLUMNS.FIRST_LOGIN') }}</th>
              <th class="py-2 pr-3">{{ t('AGENT_LOGIN_REPORTS.COLUMNS.LAST_LOGOUT') }}</th>
              <th class="py-2">{{ t('AGENT_LOGIN_REPORTS.COLUMNS.TOTAL_TIME') }}</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in rows"
              :key="`${row.agent_id}-${row.date}`"
              class="border-b border-n-weak last:border-b-0"
            >
              <td class="py-2 pr-3 text-n-slate-12">{{ row.agent_name }}</td>
              <td class="py-2 pr-3 text-n-slate-12">{{ formatDate(row.date) }}</td>
              <td class="py-2 pr-3 text-n-slate-12">{{ formatTime(row.first_login_at) }}</td>
              <td class="py-2 pr-3 text-n-slate-12">{{ formatTime(row.last_logout_at) }}</td>
              <td class="py-2 text-n-slate-11">{{ row.total_duration }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </section>
  </div>
</template>
